<script>
import { loadGoogleTaxonomy } from 'dashboard/api/pipelines/googleTaxonomyCache';

export default {
  name: 'GoogleCategoryPicker',
  props: {
    modelValue: { type: Number, default: null },
    modelPath: { type: String, default: '' },
    accountId: { type: [Number, String], required: true },
  },
  emits: ['update:modelValue', 'update:modelPath'],
  data() {
    return {
      tree: [],
      stack: [],       // breadcrumb of {name, children[]}
      search: '',
      loading: false,
      error: null,
    };
  },
  computed: {
    currentNodes() {
      if (this.search.trim()) return this.searchResults;
      if (this.stack.length === 0) return this.tree;
      return this.stack[this.stack.length - 1].children;
    },
    searchResults() {
      const term = this.search.toLowerCase();
      const results = [];
      const walk = nodes => {
        for (const node of nodes) {
          if (node.path.toLowerCase().includes(term)) results.push(node);
          if (node.children?.length) walk(node.children);
        }
      };
      walk(this.tree);
      return results;
    },
    breadcrumb() {
      return this.stack.map(n => n.name);
    },
  },
  async mounted() {
    this.loading = true;
    try {
      this.tree = await loadGoogleTaxonomy(this.accountId);
    } catch {
      this.error = 'Erro ao carregar taxonomia';
    } finally {
      this.loading = false;
    }
  },
  methods: {
    drill(node) {
      if (node.children?.length) {
        this.stack.push(node);
        this.search = '';
      } else {
        this.select(node);
      }
    },
    select(node) {
      this.$emit('update:modelValue', node.id);
      this.$emit('update:modelPath', node.path);
    },
    back() {
      this.stack.pop();
    },
    goTo(idx) {
      this.stack = this.stack.slice(0, idx + 1);
    },
    clear() {
      this.$emit('update:modelValue', null);
      this.$emit('update:modelPath', '');
      this.stack = [];
      this.search = '';
    },
  },
};
</script>

<template>
  <div class="google-category-picker border border-n-slate-6 rounded-lg p-3">
    <!-- Selected display -->
    <div v-if="modelValue" class="flex items-center gap-2 mb-2 text-sm">
      <span class="text-n-slate-11 truncate flex-1">{{ modelPath }}</span>
      <button type="button" class="text-xs text-red-500 hover:underline shrink-0" @click="clear">Remover</button>
    </div>

    <!-- Loading / Error -->
    <div v-if="loading" class="text-xs text-n-slate-11 py-2">Carregando categorias...</div>
    <div v-else-if="error" class="text-xs text-red-500 py-2">{{ error }}</div>

    <template v-else>
      <!-- Search -->
      <input
        v-model="search"
        type="text"
        class="w-full mb-2 text-sm border border-n-slate-6 rounded px-2 py-1"
        placeholder="Buscar categoria..."
      />

      <!-- Breadcrumb -->
      <div v-if="breadcrumb.length && !search" class="flex items-center gap-1 text-xs text-n-slate-11 mb-2 flex-wrap">
        <button type="button" class="hover:text-n-slate-12" @click="stack = []">Início</button>
        <template v-for="(crumb, i) in breadcrumb" :key="i">
          <span>›</span>
          <button type="button" class="hover:text-n-slate-12" @click="goTo(i)">{{ crumb }}</button>
        </template>
      </div>

      <!-- Back button -->
      <button
        v-if="stack.length && !search"
        type="button"
        class="text-xs text-blue-600 hover:underline mb-2 flex items-center gap-1"
        @click="back"
      >
        ← Voltar
      </button>

      <!-- Node list -->
      <ul class="max-h-48 overflow-y-auto space-y-0.5">
        <li
          v-for="node in currentNodes"
          :key="node.id"
          class="flex items-center justify-between px-2 py-1 rounded hover:bg-n-slate-3 cursor-pointer text-sm"
          :class="{ 'bg-n-blue-2 text-n-blue-11': node.id === modelValue }"
          @click="drill(node)"
        >
          <span class="truncate">{{ search ? node.path : node.name }}</span>
          <span v-if="node.children?.length && !search" class="text-n-slate-9 text-xs ml-2 shrink-0">›</span>
          <button
            v-else
            type="button"
            class="text-xs text-blue-600 hover:underline ml-2 shrink-0"
            @click.stop="select(node)"
          >
            Selecionar
          </button>
        </li>
        <li v-if="currentNodes.length === 0" class="text-xs text-n-slate-11 px-2 py-1">Nenhuma categoria encontrada.</li>
      </ul>
    </template>
  </div>
</template>
